import { keuanganModules } from "./fitur/keuangan";
import { kartuModules } from "./fitur/kartu";
import { akademikModules } from "./fitur/akademik";
import { kesiswaanModules } from "./fitur/kesiswaan";
import { keuanganEn } from "./fitur/en/keuangan";
import { kartuEn } from "./fitur/en/kartu";
import { akademikEn } from "./fitur/en/akademik";
import { kesiswaanEn } from "./fitur/en/kesiswaan";
import type {
  FeatureGroup,
  FeatureGroupId,
  FeatureModule,
  FeatureTranslation,
} from "./fitur/types";
import { defaultLocale, type Locale } from "./i18n";

export type {
  FeatureDetail,
  FeatureGroup,
  FeatureGroupId,
  FeatureModule,
  FeatureScreen,
  FeatureStep,
} from "./fitur/types";

/**
 * Katalog modul CARDS untuk mega menu "Fitur", halaman hub /fitur, dan
 * halaman detail /fitur/<slug>.
 *
 * Taksonomi mengikuti IA asli produk (sidebar Cards School + Semua Menu
 * Cards Parents), bukan pengelompokan marketing. Isi tiap grup ada di
 * `lib/fitur/<grup>.ts`.
 */

export const featureGroups: FeatureGroup[] = [
  { id: "keuangan", label: "Keuangan & Tagihan" },
  { id: "kartu", label: "Kartu & Transaksi" },
  { id: "akademik", label: "Akademik" },
  { id: "kesiswaan", label: "Kesiswaan & Anggota" },
];

export const featureModules: FeatureModule[] = [
  ...keuanganModules,
  ...kartuModules,
  ...akademikModules,
  ...kesiswaanModules,
];

/** Modul yang punya halaman detail sendiri. */
export const detailedModules = featureModules.filter((m) => m.detail);

export function modulesInGroup(id: FeatureGroupId): FeatureModule[] {
  return featureModules.filter((m) => m.group === id);
}

export function findModule(slug: string): FeatureModule | undefined {
  return featureModules.find((m) => m.slug === slug);
}

/**
 * Modul tanpa halaman detail diarahkan ke anchor-nya di halaman hub, supaya
 * tidak ada tautan menu yang menuju halaman kosong.
 */
export function featureHref(m: FeatureModule): string {
  return m.detail ? `/fitur/${m.slug}` : `/fitur#${m.slug}`;
}

/** Sampai tiga modul lain dalam grup yang sama, untuk blok "Fitur terkait". */
export function relatedModules(m: FeatureModule, limit = 3): FeatureModule[] {
  return modulesInGroup(m.group)
    .filter((other) => other.slug !== m.slug)
    .slice(0, limit);
}

export function groupOf(m: FeatureModule): FeatureGroup {
  return featureGroups.find((g) => g.id === m.group) ?? featureGroups[0];
}

// ---------------------------------------------------------------------------
// Terjemahan
// ---------------------------------------------------------------------------

const translations: Record<string, Record<string, FeatureTranslation>> = {
  en: { ...keuanganEn, ...kartuEn, ...akademikEn, ...kesiswaanEn },
};

/**
 * Menggabungkan terjemahan ke atas modul sumber. Gambar tetap milik data
 * Indonesia — terjemahan hanya menimpa teksnya, dicocokkan per indeks — agar
 * mengganti tangkapan layar cukup dilakukan di satu tempat.
 *
 * Modul yang belum diterjemahkan dikembalikan apa adanya, sehingga bahasa
 * yang belum lengkap menurunkan mutu halaman, bukan merusaknya.
 */
export function localizeModule(m: FeatureModule, locale: Locale): FeatureModule {
  if (locale === defaultLocale) return m;
  const t = translations[locale]?.[m.slug];
  if (!t) return m;

  const detail = m.detail
    ? {
        ...t.detail,
        screens: m.detail.screens?.map((s, i) => ({
          ...s,
          ...(t.detail.screens?.[i] ?? {}),
        })),
      }
    : undefined;

  return {
    ...m,
    label: t.label,
    navDescription: t.navDescription,
    summary: t.summary,
    detail,
  };
}

export function localizedModules(locale: Locale): FeatureModule[] {
  return featureModules.map((m) => localizeModule(m, locale));
}

export function localizedModulesInGroup(
  id: FeatureGroupId,
  locale: Locale,
): FeatureModule[] {
  return modulesInGroup(id).map((m) => localizeModule(m, locale));
}

/** Slug yang belum punya terjemahan untuk sebuah locale. */
export function untranslatedSlugs(locale: Locale): string[] {
  if (locale === defaultLocale) return [];
  const dict = translations[locale] ?? {};
  return featureModules.filter((m) => !dict[m.slug]).map((m) => m.slug);
}
